import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ShoppingBag, UploadCloud, Wrench, ArrowRight } from "lucide-react";
import SellerProductWizard from "./SellerProductWizard";
import SellerProducts from "./SellerProducts";
import SellerBulkUpload from "./SellerBulkUpload";

// Single entry point for the seller product flow: the guided wizard for adding OR editing a product,
// and a "My products" tab (list only) that opens any product straight into the wizard for editing.
export default function SellerProductHub() {
  const nav = useNavigate();
  const [tab, setTab] = useState("add");
  const [editProduct, setEditProduct] = useState(null);
  const [listKey, setListKey] = useState(0);
  const [chose, setChose] = useState(false);
  const btn = (active) =>
    `inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
      active ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"
    }`;
  const openEdit = (p) => { setEditProduct(p); setTab("add"); window.scrollTo(0, 0); };
  const finishEdit = () => { setEditProduct(null); setTab("list"); setListKey((k) => k + 1); };
  if (!chose) {
    return (
      <div data-testid="add-choice" className="mx-auto max-w-2xl py-6">
        <h2 className="text-xl font-bold text-slate-900">What would you like to add?</h2>
        <p className="mt-1 text-sm text-slate-500">Sell a product for delivery, or offer a bookable service.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <button onClick={() => setChose(true)} data-testid="choice-product" className="group rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-fuchsia-300 hover:shadow-md">
            <ShoppingBag className="h-7 w-7 text-fuchsia-500" />
            <p className="mt-3 text-base font-bold text-slate-900">Add a Product</p>
            <p className="mt-1 text-xs text-slate-500">Physical goods you ship or hand over — with price, stock and photos.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-600">Start <ArrowRight className="h-3.5 w-3.5" /></span>
          </button>
          <button onClick={() => nav("/seller/services")} data-testid="choice-service" className="group rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md">
            <Wrench className="h-7 w-7 text-sky-500" />
            <p className="mt-3 text-base font-bold text-slate-900">Add a Service</p>
            <p className="mt-1 text-xs text-slate-500">Bookable services or appointments — instant, approval or quote based.</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-600">Start <ArrowRight className="h-3.5 w-3.5" /></span>
          </button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="seller-product-hub">
      <div className="mb-4 flex flex-wrap gap-2" data-testid="product-hub-tabs">
        <button onClick={() => setChose(false)} data-testid="hub-back-choice" className={btn(false)}>← Add…</button>
        <button onClick={() => { setEditProduct(null); setTab("add"); }} data-testid="hub-tab-add" className={btn(tab === "add")}>
          <Sparkles className="h-4 w-4" /> {editProduct ? "Edit product" : "Add product"}
        </button>
        <button onClick={() => setTab("list")} data-testid="hub-tab-list" className={btn(tab === "list")}>
          <ShoppingBag className="h-4 w-4" /> My products
        </button>
        <button onClick={() => { setEditProduct(null); setTab("bulk"); }} data-testid="hub-tab-bulk" className={btn(tab === "bulk")}>
          <UploadCloud className="h-4 w-4" /> Bulk upload
        </button>
      </div>
      {tab === "add" ? (
        <SellerProductWizard key={editProduct ? editProduct.product_id : "new"} editProduct={editProduct} onDone={finishEdit} />
      ) : tab === "bulk" ? (
        <SellerBulkUpload onDone={() => { setTab("list"); setListKey((k) => k + 1); }} />
      ) : (
        <SellerProducts key={listKey} listOnly onEditInWizard={openEdit} />
      )}
    </div>
  );
}
